import { defineStore } from 'pinia'
import type { NuxtResponse, ObjectAddress } from '~/shared/api/dto'
import type { GebaeudeEigenschaften } from '~/shared/types/arcgis'
import type {
	EnergyCostCalculationResult,
	HeatingSystemConstructionYear,
	HeatingSystemHeizlast,
} from '~/shared/types/heizlast'

export interface SavedHeatingInputs {
	heatingsystem: HeatingSystemHeizlast | null
	heatingsystemConstructionYear: HeatingSystemConstructionYear | null
	verbrauch: number | null
	wohnflaeche: number | null
	consumptionFormOpen: boolean
	estimatedFields: { verbrauch: boolean; wohnflaeche: boolean }
	touchedFields: { verbrauch: boolean; wohnflaeche: boolean }
}

interface ObjectAddressMeta {
	heating: {
		heizlastResult: EnergyCostCalculationResult | null
		hwb: number | null
	}
}

const createHeatingMeta = (): ObjectAddressMeta['heating'] => ({
	heizlastResult: null,
	hwb: null,
})

interface ArcGisData {
	building: GebaeudeEigenschaften | null
}

interface ObjectStoreState {
	objectAddress: ObjectAddress | null
	arcgis: ArcGisData
	meta: ObjectAddressMeta
	heatingInputsAdrcd: string | null
	heatingInputs: SavedHeatingInputs | null
	isLoading: boolean
	error: string | null
}

export const useObjectStore = defineStore('object', {
	state: (): ObjectStoreState => ({
		objectAddress: null,
		arcgis: {
			building: null,
		},
		meta: {
			heating: createHeatingMeta(),
		},
		heatingInputsAdrcd: null,
		heatingInputs: null,
		isLoading: false,
		error: null,
	}),
	getters: {
		currentHeatingInputs(state): SavedHeatingInputs | null {
			const adrcd = state.objectAddress?.ADRCD
			if (!adrcd || state.heatingInputsAdrcd !== adrcd) return null
			return state.heatingInputs
		},
	},
	actions: {
		async fetchObjectData(adrcd: string) {
			if (this.objectAddress?.ADRCD === adrcd) return

			if (this.heatingInputsAdrcd !== adrcd) {
				this.heatingInputs = null
				this.heatingInputsAdrcd = null
			}

			this.meta.heating = createHeatingMeta()
			this.arcgis.building = null
			this.isLoading = true
			this.error = null

			try {
				const res = await useFetch<NuxtResponse<ObjectAddress>>('/api/arcgis/object', {
					key: `object-${adrcd}`,
					params: { adrcd },
				})

				if (!res.data.value) {
					throw new Error('No response data received')
				}

				const response = res.data.value

				if (response.status === 404) {
					this.error = 'Address not found'
					throw new Error('Address not found')
				}

				if (response.status === 400) {
					this.error = 'Invalid address code'
					throw new Error('Invalid address code')
				}

				if (response.status >= 500) {
					this.error = 'Server error'
					throw new Error('Server error')
				}

				if (!response.data) {
					this.error = 'Address not found'
					throw new Error('Address not found')
				}

				this.objectAddress = response.data
				await this.fetchBuildingProperties()
			} catch (error) {
				console.log('Error fetching object data:', error)
				if (!this.error) {
					this.error = 'Failed to fetch object data'
				}
				throw error
			} finally {
				this.isLoading = false
			}
		},
		async fetchBuildingProperties() {
			const address = this.objectAddress
			if (!address || !Number.isFinite(address.X) || !Number.isFinite(address.Y)) return

			try {
				const res = await $fetch<NuxtResponse<GebaeudeEigenschaften>>('/api/arcgis/object/building-properties', {
					query: { X: address.X, Y: address.Y },
				})
				this.arcgis.building = res.status === 200 ? res.data : null
			} catch (error) {
				console.error('Error fetching building properties:', error)
				this.arcgis.building = null
			}
		},
		saveHeatingInputs(adrcd: string, inputs: SavedHeatingInputs) {
			this.heatingInputsAdrcd = adrcd
			this.heatingInputs = inputs
		},
	},
	persist: {
		pick: ['heatingInputsAdrcd', 'heatingInputs'],
	},
})
